import { Box, Heading, useDisclosure } from "@chakra-ui/react";
import { ElementType, ReactNode, RefObject } from "react";
import { FaEnvelope, FaGoogle, FaTelegramPlane } from "react-icons/fa";
import { useIntl } from "react-intl";
import {
  METHOD_KEYS,
  MethodKey,
  SignInMethodsData,
  SignInMethodStatus,
} from "api/SignInMethods";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";
import { telegramLoginClientId } from "hooks/useTelegramOidcPopup";
import { FOCUS_RING } from "utils/chackra/controlStyles";
import EmailLinkModal from "./EmailLinkModal";
import GoogleLinkModal from "./GoogleLinkModal";
import TelegramLinkModal from "./TelegramLinkModal";
import MethodIcon, { MethodIconState } from "./MethodIcon";

const METHOD_ICONS: Record<MethodKey, ElementType> = {
  email: FaEnvelope,
  google: FaGoogle,
  telegram: FaTelegramPlane,
};

const resolveState = (key: MethodKey, method: SignInMethodStatus): MethodIconState => {
  if (method.linked) return "linked";
  if (!method.linkable) return "unavailable";
  if (key === "google" && !process.env.REACT_APP_GAPI_CLIENT_ID) return "unavailable";
  if (key === "telegram" && !telegramLoginClientId()) return "unavailable";
  return "linkable";
};

/** The card frame. ProfileInfo also uses it for the loading and error lines,
 *  so an unknown status still sits under the same title. The heading takes focus
 *  when the control that held it disappears, so focus never falls to the body. */
export const SignInMethodsFrame = ({
  children,
  headingRef,
}: {
  children: ReactNode;
  headingRef?: RefObject<HTMLHeadingElement>;
}) => {
  const intl = useIntl();
  return (
    <BoxShadowContainer>
      <Heading
        as="h2"
        ref={headingRef}
        tabIndex={-1}
        fontSize="xl"
        fontWeight="semibold"
        color="text.primary"
        mb={4}
        _focusVisible={FOCUS_RING}
      >
        {intl.formatMessage({ id: "signInMethods.title" })}
      </Heading>
      {children}
    </BoxShadowContainer>
  );
};

interface SignInMethodsProps {
  methods: SignInMethodsData;
  /** The account this page rendered; the server refuses a link for any other. */
  userId: number;
  /** Runs after the dialog has closed, when the card must catch up with the server. */
  onLinked: () => void;
  headingRef?: RefObject<HTMLHeadingElement>;
  /** Whether a dialog is up, from opening until its exit has finished. The card
   *  must stay mounted that long: unmounting it would take the dialog with it. */
  onDialogActive?: (isActive: boolean) => void;
}

const SignInMethods = ({
  methods,
  userId,
  onLinked,
  headingRef,
  onDialogActive,
}: SignInMethodsProps) => {
  const intl = useIntl();
  const email = useDisclosure();
  const google = useDisclosure();
  const telegram = useDisclosure();
  const telegramClientId = telegramLoginClientId();
  const opener = (open: () => void) => () => {
    onDialogActive?.(true);
    open();
  };
  const onDialogGone = () => onDialogActive?.(false);
  const openers: Partial<Record<MethodKey, () => void>> = {
    email: opener(email.onOpen),
    google: opener(google.onOpen),
    telegram: opener(telegram.onOpen),
  };

  return (
    <SignInMethodsFrame headingRef={headingRef}>
      <Box
        as="ul"
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(5.5rem, 9rem))"
        gap={3}
        listStyleType="none"
      >
        {METHOD_KEYS.map((key) => (
          <li key={key}>
            <MethodIcon
              icon={METHOD_ICONS[key]}
              label={intl.formatMessage({ id: `signInMethods.${key}` })}
              state={resolveState(key, methods[key])}
              display={methods[key].display}
              onClick={openers[key]}
            />
          </li>
        ))}
      </Box>
      <EmailLinkModal
        isOpen={email.isOpen}
        onClose={email.onClose}
        userId={userId}
        onLinked={onLinked}
        onClosed={onDialogGone}
      />
      <GoogleLinkModal
        isOpen={google.isOpen}
        onClose={google.onClose}
        userId={userId}
        onLinked={onLinked}
        onClosed={onDialogGone}
      />
      {telegramClientId && (
        <TelegramLinkModal
          isOpen={telegram.isOpen}
          onClose={telegram.onClose}
          clientId={telegramClientId}
          userId={userId}
          onLinked={onLinked}
          onClosed={onDialogGone}
        />
      )}
    </SignInMethodsFrame>
  );
};

export default SignInMethods;
